import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQueryClient } from '@tanstack/react-query';
import useApi from '../../../hooks/useApi';
import { sanitizeRecords, validateVmtRecord } from '../../../utils/vmt';
import { normalizeDesignationLabel, normalizeImrUsage, normalizeLifecycleStatus } from '../../../utils/vmtShared';
import { buildUnmatchReferenceId } from '../../../utils/vmtQuickActions';
import { buildAppsheetVmtRecord } from './appsheetVmtMapping';
import SelectionFormGrid from '../../product-selection-send/SelectionFormGrid.jsx';

const normalizeOccurrence = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Monthly';
  const key = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').toLowerCase();
  const aliases = {
    'one time': 'One-Time',
    'one-time': 'One-Time',
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    'semi annual': 'Semi-Annual',
    'semi-annual': 'Semi-Annual',
    annual: 'Annual',
  };
  return aliases[key] || raw;
};

const buildInitialRecord = (row, { defaults, overrides, userEmail, variant, programName }) => {
  const mapped = buildAppsheetVmtRecord(row, {
    defaults,
    overrides,
    stampLastUpdatedDate: true,
  });
  const record = sanitizeRecords([mapped])[0];
  const resolvedProgramName = overrides?.programName || programName || row?.programName || row?.programLabel || (variant === 'trade' ? 'Trade' : '');
  if (resolvedProgramName) record.programName = resolvedProgramName;
  record.status = normalizeLifecycleStatus(record.status) || 'Live';
  record.designation = normalizeDesignationLabel(record.designation, variant === 'trade' ? 'Package' : '');
  record.imrUsage = normalizeImrUsage(record.imrUsage);
  record.occurrence = normalizeOccurrence(record.occurrence);
  if (!record.lastUpdatedBy && userEmail) record.lastUpdatedBy = userEmail;
  return record;
};

const AppSheetEditAndSendVmt = ({
  row,
  bac,
  userEmail = null,
  variant = 'appsheet',
  programName = null,
  defaults = null,
  overrides = null,
  onBack,
  onNotify,
}) => {
  const queryClient = useQueryClient();
  const { useVmtSend } = useApi();
  const sendMutation = useVmtSend({
    sendMode: variant === 'trade' || overrides?.programName === 'Trade' ? 'trade' : 'general',
  });
  const initialFormValues = useMemo(
    () => buildInitialRecord(row || {}, { defaults, overrides, userEmail, variant, programName }),
    [defaults, overrides, programName, row, userEmail, variant]
  );
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendAccepted, setSendAccepted] = useState(false);
  const [sendQueued, setSendQueued] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const sendStartedRef = useRef(false);

  const itemLabel = variant === 'trade' ? 'Trade line item' : 'AppSheet order item';
  const title = `Edit & Send VMT — ${itemLabel}`;

  const handleFieldChange = useCallback((field, value) => {
    setFormValues((previous) => ({ ...previous, [field]: field === 'imrUsage' ? String(value || '').toUpperCase() : value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  }, []);

  const handleDateChange = useCallback((field, value) => {
    if (!value || !value.isValid()) {
      handleFieldChange(field, '');
      return;
    }
    handleFieldChange(field, value.format('MM/DD/YY'));
  }, [handleFieldChange]);

  const handleSend = useCallback(async () => {
    if (sendStartedRef.current || sendAccepted || sendQueued) return;
    sendStartedRef.current = true;
    setIsSending(true);
    const sanitized = sanitizeRecords([{ ...formValues, bac: formValues.bac || bac }])[0];
    const validation = validateVmtRecord(sanitized);
    if (Object.keys(validation).length) {
      setErrors(validation);
      setSnackbar({ open: true, message: 'Complete the required VMT fields before sending.', severity: 'error' });
      sendStartedRef.current = false;
      setIsSending(false);
      return;
    }
    setErrors({});
    let result;
    try {
      result = await sendMutation.mutateAsync({ records: [sanitized], userEmail });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Failed to send to VMT.';
      setSnackbar({ open: true, message, severity: 'error' });
      sendStartedRef.current = false;
      setIsSending(false);
      return;
    }

    if (result?.queued) {
      setSnackbar({ open: true, message: 'VMT send queued for offline sync.', severity: 'info' });
      setSendQueued(true);
      sendStartedRef.current = false;
      setIsSending(false);
      return;
    }

    // The outbound write has succeeded at this point. History cache refresh is
    // useful UI work, but it must not turn a successful production send into a
    // false failure or suppress the required single-dealer refresh.
    try {
      await queryClient.invalidateQueries({ queryKey: ['vmtSendHistory'] });
    } catch (error) {
      console.warn('[AppSheetEditAndSendVmt] send history invalidation failed', error);
    }

    const snapshotStatus = result?.snapshot?.snapshotStatus;
    const snapshotWarning = snapshotStatus === 'failed'
      ? `${itemLabel} sent to VMT, but snapshot capture failed.`
      : snapshotStatus === 'partial'
        ? `${itemLabel} sent to VMT; snapshot capture partially failed.`
        : null;
    const successMessage = snapshotWarning || `${itemLabel} sent to VMT.`;
    const successSeverity = snapshotWarning ? 'warning' : 'success';
    setSnackbar({ open: true, message: successMessage, severity: successSeverity });
    sendStartedRef.current = false;
    setIsSending(false);
    setSendAccepted(true);
    try {
      onNotify?.(successMessage, successSeverity, {
        refreshDealer: true,
        refreshProgramName: sanitized.programName || (variant === 'trade' ? 'Trade' : null),
        refreshBac: bac || sanitized.bac,
        suppressSnackbar: true,
      });
    } catch (error) {
      // Notification/refresh wiring is downstream of the already-accepted send.
      // Keep the local success state truthful if an integration callback fails.
      console.error('[AppSheetEditAndSendVmt] post-send notification failed', error);
    }
  }, [bac, formValues, itemLabel, onNotify, queryClient, sendAccepted, sendMutation, sendQueued, userEmail, variant]);

  const handleUnmatchReferenceId = useCallback(() => {
    const uniqueValue = buildUnmatchReferenceId(bac || formValues.bac);
    handleFieldChange('referenceId', uniqueValue);
  }, [bac, formValues.bac, handleFieldChange]);

  const handleReset = useCallback(() => {
    setFormValues(initialFormValues);
    setErrors({});
    setSendAccepted(false);
    setSendQueued(false);
    setIsSending(false);
    sendStartedRef.current = false;
  }, [initialFormValues]);

  const isBusy = Boolean(sendMutation.isPending) || isSending;
  const sourceRowId = row?.Id || row?.id || row?.__rowId || '';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box data-testid="appsheet-edit-send-vmt" sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            {sourceRowId ? <Typography variant="caption" color="text.secondary">Order item: {sourceRowId}</Typography> : null}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleReset} disabled={isBusy}>Reset</Button>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} disabled={isBusy}>Back</Button>
          </Stack>
        </Stack>
        <Alert severity="info" sx={{ mb: 2 }}>
          Review and correct the outbound VMT record. Changes apply to this send only; the AppSheet source row is not edited.
        </Alert>
        <SelectionFormGrid
          formValues={formValues}
          errors={errors}
          onFieldChange={handleFieldChange}
          onDateChange={handleDateChange}
          onUnmatchReferenceId={handleUnmatchReferenceId}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            data-testid="appsheet-edit-send-vmt-submit"
            variant="contained"
            startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={isBusy || sendAccepted || sendQueued}
            onClick={handleSend}
          >
            {isBusy ? 'Sending…' : sendAccepted ? 'Sent to VMT' : sendQueued ? 'Queued for sync' : 'Send to VMT'}
          </Button>
        </Stack>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3200}
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        >
          <Alert onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AppSheetEditAndSendVmt;
